import { Button } from "@/components/ui/button"
import { Progress } from "@/components/ui/progress"
import { Skeleton } from "@/components/ui/skeleton"
import { Typography } from "@/components/ui/typography"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { Check, CircleAlert } from "lucide-react"
import { Link } from "react-router"
import { LanguageStatus } from "../../components/LanguageStatus"
import {
  NeedsSetupBadge,
  SettingsPage,
  SettingsSection,
} from "../../components/SettingsPage"
import {
  useSettingsSetup,
  type SetupItem,
  type SetupItemId,
} from "../../setup/useSettingsSetup"
import { useSettingsVisibility } from "../../useSettingsVisibility"

const ITEM_COPY: Record<
  SetupItemId,
  { title: TranslationKeys; description: TranslationKeys }
> = {
  companyDetails: {
    title: "SETTINGS/SETUP/COMPANY_DETAILS",
    description: "SETTINGS/SETUP/COMPANY_DETAILS_DESCRIPTION",
  },
  logo: {
    title: "SETTINGS/SETUP/LOGO",
    description: "SETTINGS/SETUP/LOGO_DESCRIPTION",
  },
  sendingAddress: {
    title: "SETTINGS/EMAIL_SENDING/ADDRESS_TITLE",
    description: "SETTINGS/SETUP/SENDING_ADDRESS_DESCRIPTION",
  },
  emailSignature: {
    title: "SETTINGS/EMAIL_SENDING/SIGNATURE_TITLE",
    description: "SETTINGS/SETUP/EMAIL_SIGNATURE_DESCRIPTION",
  },
  pdfFooter: {
    title: "MC_TPL_FOOTER_LABEL",
    description: "SETTINGS/SETUP/PDF_FOOTER_DESCRIPTION",
  },
  signatureImage: {
    title: "SETTINGS/SETUP/SIGNATURE_IMAGE",
    description: "SETTINGS/SETUP/SIGNATURE_IMAGE_DESCRIPTION",
  },
}

function ItemStatusIcon({ state }: { state: SetupItem["state"] }) {
  if (state === "done")
    return (
      <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
        <Check aria-hidden className="size-3.5" />
      </span>
    )
  if (state === "error")
    return (
      <CircleAlert
        aria-hidden
        className="size-6 shrink-0 text-muted-foreground"
      />
    )
  return (
    <span
      aria-hidden
      className="size-6 shrink-0 rounded-full border-2 border-dashed border-warning/60"
    />
  )
}

function SetupRow({ item, onRetry }: { item: SetupItem; onRetry: () => void }) {
  const { translate } = useTranslation()
  const copy = ITEM_COPY[item.id]
  const title = translate(copy.title)
  const action = translate(
    item.state === "todo" ? "SETTINGS/SETUP/SET_UP" : "SETTINGS/SETUP/REVIEW",
  )

  return (
    // In a narrow column (the assistant panel open beside it) the button
    // goes under the text instead of squeezing it.
    <li className="flex flex-col gap-3 py-4 first:pt-0 last:pb-0 @lg:flex-row @lg:items-start @lg:gap-4">
      <div className="flex min-w-0 flex-1 items-start gap-4">
        {/* As tall as the title's line (leading-7), so the icon centres on
            the title rather than on the top of the row. */}
        <div className="flex h-7 shrink-0 items-center">
          <ItemStatusIcon state={item.state} />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <Typography as="p" className="font-medium">
              {title}
            </Typography>
            {item.state === "todo" && <NeedsSetupBadge />}
          </div>
          <Typography as="muted">{translate(copy.description)}</Typography>
          {item.languages && <LanguageStatus needsWriting={item.languages} />}
          {item.state === "error" && (
            <Typography as="muted" className="text-xs">
              {translate("SETTINGS/SETUP/CHECK_FAILED")}
            </Typography>
          )}
        </div>
      </div>
      <div className="shrink-0 pl-10 @lg:pl-0">
        {item.state === "loading" ? (
          <Skeleton className="h-9 w-20" aria-busy="true" />
        ) : item.state === "error" ? (
          <Button variant="outline" size="sm" onClick={onRetry}>
            {translate("SETTINGS/RETRY")}
          </Button>
        ) : (
          <Button
            variant={item.state === "todo" ? "default" : "outline"}
            size="sm"
            asChild
          >
            <Link to={item.path} replace aria-label={`${action}: ${title}`}>
              {action}
            </Link>
          </Button>
        )}
      </div>
    </li>
  )
}

/**
 * The settings home: everything the org sets once so its documents and
 * emails look like they come from it, each with whether it is done and the
 * way to it. The same checks mark the pages in the nav.
 */
export function SetupChecklistPage() {
  const { translate } = useTranslation()
  const { items, retry } = useSettingsSetup(useSettingsVisibility())
  const done = items.filter((item) => item.state === "done").length
  const allDone = items.length > 0 && done === items.length

  return (
    <SettingsPage
      title={translate("SETTINGS/SETUP/TITLE")}
      description={translate("SETTINGS/SETUP/DESCRIPTION")}
    >
      <SettingsSection>
        <div className="flex flex-col gap-2">
          <Typography as="p" className="font-medium">
            {allDone
              ? translate("SETTINGS/SETUP/ALL_DONE")
              : translate("SETTINGS/SETUP/PROGRESS", {
                  done,
                  total: items.length,
                })}
          </Typography>
          <Progress
            value={items.length ? (done / items.length) * 100 : 0}
            aria-label={translate("SETTINGS/SETUP/PROGRESS", {
              done,
              total: items.length,
            })}
          />
        </div>
      </SettingsSection>
      <SettingsSection>
        <ul className="@container flex flex-col divide-y">
          {items.map((item) => (
            <SetupRow key={item.id} item={item} onRetry={retry} />
          ))}
        </ul>
      </SettingsSection>
    </SettingsPage>
  )
}
